import { GiftCouponRedeemStatus } from '@shared/api/giftCoupon/giftCoupon.interface';

export const getRedeemStatusMessage = (status: GiftCouponRedeemStatus) => {
  switch (status) {
    case GiftCouponRedeemStatus.Available:
      return null;
    case GiftCouponRedeemStatus.Requested:
      return {
        type: 'info' as const,
        title: '신청이 완료되었습니다',
        message:
          '입력하신 번호로 곧 전송됩니다.\n잠시만 기다려 주세요.\n\n영업일 기준 10시, 14시, 18시에 발송됩니다.\n발송까지 30분~1시간 소요될 수 있습니다.\n(주말 및 공휴일 제외, 익영업일 기준)',
      };
    case GiftCouponRedeemStatus.Delivered:
      return {
        type: 'success' as const,
        title: '전달이 완료되었습니다',
        message: '',
      };
    case GiftCouponRedeemStatus.Expired:
      return {
        type: 'error' as const,
        title: '유효기간 만료',
        message: '이 답례품은 유효기간이 지났습니다.',
      };
    case GiftCouponRedeemStatus.Refunded:
      return {
        type: 'error' as const,
        title: '환불 완료',
        message: '이 답례품은 환불 처리되었습니다.',
        hideContact: true,
      };
    case GiftCouponRedeemStatus.Canceled:
      return {
        type: 'error' as const,
        title: '취소됨',
        message: '이 답례품은 취소되었습니다.',
        hideContact: true,
      };
    case GiftCouponRedeemStatus.Disposed:
      return {
        type: 'info' as const,
        title: '이 쿠폰은 사용불가 처리되었습니다',
        message: '이 쿠폰은 더 이상 사용할 수 없습니다.\n불편을 드려 죄송합니다.',
        hideContact: true,
      };
    default:
      return null;
  }
};

export type RedeemStatusMessage = NonNullable<ReturnType<typeof getRedeemStatusMessage>>;

// 이 기기에서 해당 쿠폰 페이지로 신청한 기록 — 추가교환(extra) 폼 노출 게이팅용.
// 정상신청·나중에받기·추가신청 성공 시 모두 기록한다. 기록이 없는 "신규 유입자"에게만
// (추가교환이 열려있을 때) 신청 폼을 다시 노출한다.
const COUPON_APPLIED_KEY_PREFIX = 'giftCouponApplied:';

export const markCouponApplied = (code: string) => {
  try {
    localStorage.setItem(`${COUPON_APPLIED_KEY_PREFIX}${code}`, new Date().toISOString());
  } catch {
    // localStorage 미사용 환경(시크릿 등) 무시
  }
};

export const hasCouponApplied = (code: string): boolean => {
  try {
    return !!localStorage.getItem(`${COUPON_APPLIED_KEY_PREFIX}${code}`);
  } catch {
    return false;
  }
};

// 추가교환(extra)에서 발급된 본인 spawn 쿠폰 매핑 — 원본코드 → spawn코드.
// 원본(중복노출) URL 재방문 시 본인 spawn 쿠폰 URL로 리다이렉트하는 데 쓴다.
const SPAWN_MAP_KEY_PREFIX = 'giftCouponSpawn:';

export const setSpawnMapping = (originalCode: string, spawnedCode: string) => {
  try {
    localStorage.setItem(`${SPAWN_MAP_KEY_PREFIX}${originalCode}`, spawnedCode);
  } catch {
    // 무시
  }
};

export const getSpawnMapping = (originalCode: string): string | null => {
  try {
    return localStorage.getItem(`${SPAWN_MAP_KEY_PREFIX}${originalCode}`);
  } catch {
    return null;
  }
};

export const formatPhoneNumber = (value: string) => {
  const numbers = value.replace(/[^0-9]/g, '');
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
};

/**
 * 두 문자열의 Levenshtein 편집거리(삽입/삭제/치환 1회당 1) 를 계산한다.
 * 문자열이 짧다는(<= 11자) 가정 하에 단순 DP 로 구현.
 */
const levenshteinDistance = (a: string, b: string): number => {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let prev: number[] = Array.from({ length: b.length + 1 }, (_, j) => j);
  let curr: number[] = new Array<number>(b.length + 1).fill(0);

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        (curr[j - 1] as number) + 1, // insertion
        (prev[j] as number) + 1, // deletion
        (prev[j - 1] as number) + cost, // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length] as number;
};

/** 허용 가능한 편집거리. 한두 글자 오타까지는 통과시키기 위함. */
export const RESTRICTED_PHONE_ALLOWED_EDIT_DISTANCE = 2;

/**
 * 패턴을 "전체 번호"로 간주하기 위한 최소 자릿수.
 * 한국 휴대폰은 10~11자리이므로, 이 미만이면 부분(부분 자릿수) 패턴으로 본다.
 */
const FULL_PHONE_MIN_LENGTH = 10;

/**
 * `additionalRecipientPhone`(giftCoupon / giftDelivery 회수 잠금 번호) 가 등록되어 있을
 * 때 입력 번호가 허용되는지 검사한다.
 *
 * - 양쪽 모두 숫자만 남긴 뒤 비교한다.
 * - 등록 패턴이 전체 휴대폰 번호 길이 미만(예: `9530` 같은 4자리)이면
 *   입력 번호에 패턴이 부분 문자열로 포함되는지(`includes`) 검사한다.
 *   → 관리자가 번호 일부만 등록해 두는 경우를 허용한다.
 * - 등록 패턴이 전체 휴대폰 번호 길이라면 Levenshtein 편집거리 ≤
 *   `RESTRICTED_PHONE_ALLOWED_EDIT_DISTANCE` 인 경우 허용한다.
 *   (구매자가 한두 자리 오타로 알려준 케이스까지 통과)
 * - 등록 번호가 없으면 항상 허용(true).
 */
export const isPhoneAllowedByRestrictedPhone = (
  inputPhone: string,
  additionalRecipientPhone?: string | null,
): boolean => {
  if (!additionalRecipientPhone) return true;
  const cleanedInput = inputPhone.replace(/[^0-9]/g, '');
  const cleanedPattern = additionalRecipientPhone.replace(/[^0-9]/g, '');
  if (!cleanedPattern) return true;
  if (cleanedInput === cleanedPattern) return true;

  // 부분 패턴: 입력 번호 어딘가에 패턴이 포함되면 허용
  if (cleanedPattern.length < FULL_PHONE_MIN_LENGTH) {
    return cleanedInput.includes(cleanedPattern);
  }

  return (
    levenshteinDistance(cleanedInput, cleanedPattern) <=
    RESTRICTED_PHONE_ALLOWED_EDIT_DISTANCE
  );
};

export const RESTRICTED_PHONE_ERROR_MESSAGE = '해당 쿠폰을 사용할 수 없는 번호입니다.';

// 한국 공휴일 — 'MM-DD' 형식, 연도별 관리
const KOREAN_HOLIDAYS: Record<number, string[]> = {
  2026: [
    '01-01', '02-16', '02-17', '02-18', '03-01', '03-02',
    '05-05', '05-24', '05-25', '06-06', '08-15', '08-17',
    '09-24', '09-25', '09-26', '10-03', '10-05', '10-09', '12-25',
  ],
  2027: [
    '01-01', '02-06', '02-07', '02-08', '02-09', '03-01',
    '05-05', '05-13', '06-06', '08-15', '08-16',
    '09-14', '09-15', '09-16', '10-03', '10-04', '10-09', '10-11',
    '12-25', '12-27',
  ],
  2028: [
    '01-01', '01-26', '01-27', '01-28', '03-01',
    '05-02', '05-05', '06-06', '08-15',
    '10-02', '10-03', '10-04', '10-05', '10-09', '12-25',
  ],
};

const toDateKey = (d: Date) => {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${m}-${day}`;
};

const isHolidayOrWeekend = (d: Date) => {
  const dow = d.getDay();
  if (dow === 0 || dow === 6) return true;
  const holidays = KOREAN_HOLIDAYS[d.getFullYear()];
  if (!holidays) return false;
  return holidays.includes(toDateKey(d));
};

const COUPON_SEND_HOURS = [10, 14, 18] as const;

const nextBusinessDay = (d: Date): Date => {
  const n = new Date(d);
  n.setDate(n.getDate() + 1);
  while (isHolidayOrWeekend(n)) {
    n.setDate(n.getDate() + 1);
  }
  return n;
};

export const getNextCouponSendSlot = (at: Date): Date => {
  const d = new Date(at);

  if (isHolidayOrWeekend(d)) {
    const bd = nextBusinessDay(d);
    bd.setHours(COUPON_SEND_HOURS[0], 0, 0, 0);
    return bd;
  }

  const atMs = d.getTime();
  for (const h of COUPON_SEND_HOURS) {
    const slot = new Date(d);
    slot.setHours(h, 0, 0, 0);
    if (atMs <= slot.getTime()) {
      return slot;
    }
  }

  const bd = nextBusinessDay(d);
  bd.setHours(COUPON_SEND_HOURS[0], 0, 0, 0);
  return bd;
};

/**
 * 해당 신청 건을 "지금 발송해야 하는지" 여부.
 * 신청 시각 기준 다음 발송 슬롯(영업일 10/14/18시)이 현재 시각에 도달(경과)했으면 true.
 * 예) 09:00 신청 → 슬롯 10:00 → 현재 10:00 이상이면 발송 대상.
 *     11:00 신청 → 슬롯 14:00 → 현재 13:00이면 아직 아님(2시 발송 건).
 */
export const isCouponSendDue = (
  requestedAt: Date | number,
  now: Date = new Date(),
): boolean => {
  const slot = getNextCouponSendSlot(new Date(requestedAt)).getTime();
  return isFinite(slot) && slot <= now.getTime();
};

export const formatKoreanDate = (date: Date) =>
  date.toLocaleDateString('ko-KR', {
    month: 'long',
    day: 'numeric',
    weekday: 'short',
  });

export const formatKoreanTime = (date: Date) =>
  date.toLocaleTimeString('ko-KR', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

export const formatSendTimeRange = (slot: Date) => {
  const end = new Date(slot);
  end.setHours(end.getHours() + 1);
  return `${formatKoreanTime(slot)} ~ ${formatKoreanTime(end)}`;
};
